import React from 'react'
import { IconResizeHandle } from '../../icons/resizeHandle'

/**
 * A resizable component that allows the user to resize it by dragging the right edge.
 * It uses a custom CSS variable to set the width of the component internally. It only applies the width when not on mobile.
 *
 * @param {number} minWidth - The minimum width of the component.
 * @param {number} maxWidth - The maximum width of the component.
 * @param {number} initialWidth - The initial width of the component.
 * @param {number} screenEdgeBuffer - How far from the right edge of the screen the component should stop resizing.
 * @param {string} className - Additional class names to add to the component.
 * @param {boolean} enabled - Whether resizing is enabled or not.
 */
export const Resizable: React.FC<{
  className?: string
  children: React.ReactNode
  minWidth?: number
  maxWidth?: number
  initialWidth?: number
  screenEdgeBuffer?: number
  enabled?: boolean
}> = ({
  children,
  minWidth = 0,
  maxWidth = 9999,
  initialWidth = -1,
  screenEdgeBuffer = 0,
  className = '',
  enabled = true,
}) => {
  const [width, setWidth] = React.useState<number>(initialWidth)

  const measuredRef = React.useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      setWidth(node.offsetWidth)
    }
  }, [])

  return (
    <div
      className={`relative custom-width ${className}`}
      ref={measuredRef}
      /* @ts-ignore */
      style={enabled && width >= 0 ? { '--custom-width': `${width}px` } : {}}
    >
      {children}
      {enabled && (
        <div
          className="absolute right-[-4px] top-0 z-10 h-full w-2 sm:cursor-col-resize"
          onMouseDown={(e) => {
            e.preventDefault()
            const startX = e.pageX
            const startWidth = width
            const left = startX - startWidth
            const onMouseMove = (e: MouseEvent) => {
              const screenMax = window.innerWidth - screenEdgeBuffer - left
              const newWidth = Math.min(
                Math.max(minWidth, startWidth + e.pageX - startX),
                Math.min(maxWidth, screenMax)
              )
              setWidth(newWidth)
            }
            const onMouseUp = () => {
              window.removeEventListener('mousemove', onMouseMove)
              window.removeEventListener('mouseup', onMouseUp)
            }

            window.addEventListener('mousemove', onMouseMove)
            window.addEventListener('mouseup', onMouseUp)
          }}
        />
      )}
    </div>
  )
}

/**
 * A resizable component that allows the user to resize it by dragging the bottom right corner.
 * It uses a custom CSS variable to set the width and height of the component internally. It only applies the width when not on mobile.
 *
 * @param {number} minWidth - The minimum width of the component.
 * @param {number} minHeight - The minimum height of the component.
 * @param {number} maxWidth - The maximum width of the component.
 * @param {number} maxHeight - The maximum height of the component.
 * @param {string} className - Additional class names to add to the component.
 * @param {boolean} active - Whether resizing is enabled or not.
 */
export const BottomRightResizable: React.FC<{
  children: React.ReactNode
  className?: string
  minWidth?: number | 'initial'
  minHeight?: number | 'initial'
  maxWidth?: number | string
  maxHeight?: number | string
  active?: boolean
  resizeScaleX?: number
}> = ({
  children,
  className,
  minWidth = 0,
  minHeight = 0,
  maxWidth = 9999,
  maxHeight = 9999,
  active = true,
  resizeScaleX = 1,
  ...props
}) => {
  const [width, setWidth] = React.useState<number>(-1)
  const [height, setHeight] = React.useState<number>(-1)
  const initialWidth = React.useRef<number>(-1)
  const initialHeight = React.useRef<number>(-1)
  const outerResizeRef = React.useRef<HTMLDivElement>(null)
  const mouseDownRef = React.useRef(false)

  React.useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (!mouseDownRef.current && outerResizeRef.current && outerResizeRef.current.offsetWidth > width) {
        setWidth(outerResizeRef.current.offsetWidth)
      }
    })
    observer.observe(outerResizeRef.current!)
    return () => observer.disconnect()
  }, [])

  const measuredRef = React.useCallback((node: HTMLDivElement) => {
    if (node !== null) {
      initialWidth.current = node.offsetWidth
      initialHeight.current = node.offsetHeight
      setWidth(node.offsetWidth)
      setHeight(node.offsetHeight)
    }
  }, [])

  const minWidthReal = typeof minWidth === 'number' ? minWidth : initialWidth.current
  const minHeightReal = typeof minHeight === 'number' ? minHeight : initialHeight.current
  return (
    <div className="flex-grow" ref={outerResizeRef}>
      <div
        className={`relative custom-width ${className}`}
        ref={measuredRef}
        style={
          active
            ? {
                // @ts-ignore
                '--custom-width': Math.max(minWidthReal, width) + 'px',
                minHeight: Math.max(minHeightReal, height),
              }
            : {}
        }
        {...props}
      >
        {children}
        <IconResizeHandle
          className="absolute bottom-1 right-1 z-10 h-3 w-3 cursor-se-resize opacity-30 -sm:hidden"
          onMouseDown={(e) => {
            e.preventDefault()
            const startX = e.clientX
            const startY = e.clientY
            const startWidth = width
            const startHeight = height
            const onMouseMove = (e: MouseEvent) => {
              // if maxWidth is in vw/vh, we need to calculate the real max width
              const realMaxWidth =
                typeof maxWidth === 'string' ? (window.innerWidth * parseInt(maxWidth)) / 100 : maxWidth
              const realMaxHeight =
                typeof maxHeight === 'string' ? (window.innerHeight * parseInt(maxHeight)) / 100 : maxHeight

              const newWidth = Math.min(
                realMaxWidth,
                Math.max(minWidthReal, startWidth + (e.clientX - startX) * resizeScaleX)
              )
              const newHeight = Math.min(realMaxHeight, Math.max(minHeightReal, startHeight + e.clientY - startY))
              setWidth(newWidth)
              setHeight(newHeight)
            }
            const onMouseUp = () => {
              mouseDownRef.current = false
              window.removeEventListener('mousemove', onMouseMove)
              window.removeEventListener('mouseup', onMouseUp)
            }

            mouseDownRef.current = true
            window.addEventListener('mousemove', onMouseMove)
            window.addEventListener('mouseup', onMouseUp)
          }}
        />
      </div>
    </div>
  )
}
